import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ReactSVG } from 'react-svg';
import { clsx } from 'clsx';
import { TFunction } from 'i18next';
import { fDateTime, fToCurrency } from '@web/utils';
import arrowTop from '@web/utils/src/assets/icons/functions/arrow-top.svg';
import { StyledTable } from '@/commons/ui/theme/materialStyles';
import { Order, OrderStatus } from '@/types';
import { Box, TableCell, TableRow, TableSortLabel } from '@mui/material';

export type OOrder = Pick<
  Order,
  'id' | 'status' | 'createdAt' | 'totalNet' | 'confirmedAt'
>;

interface UserOrdersTableProps {
  orders: OOrder[];
}

type TableKey = keyof Order;

const TABLE_HEAD = (
  t: TFunction,
): {
  id: TableKey;
  label: string;
  alignRight: boolean;
}[] => [
  {
    id: 'id',
    label: t('orders.table_columns.order_id'),
    alignRight: false,
  },
  {
    id: 'status',
    label: t('orders.table_columns.payment_status'),
    alignRight: false,
  },
  {
    id: 'createdAt',
    label: t('orders.table_columns.created_at'),
    alignRight: false,
  },
  {
    id: 'totalNet',
    label: t('orders.table_columns.order_total_net'),
    alignRight: false,
  },
];

const visuallyHidden = {
  border: 0,
  margin: -1,
  padding: 0,
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  position: 'absolute',
  whiteSpace: 'nowrap',
  clip: 'rect(0 0 0 0)',
};

export const getStylesByColor = (status: OrderStatus) => {
  switch (status) {
    case OrderStatus.Cancelled:
      return 'bg-red-100 text-[#B54708]';
    case OrderStatus.Pending:
      return 'bg-secondary-yellow text-[#B54708]';
    case OrderStatus.Complete:
      return 'bg-secondary-green text-primary-green';
    case OrderStatus.Confirmed:
      return 'bg-secondary-blue text-primary-blue';
  }
};

export default function UserOrdersTable(props: UserOrdersTableProps) {
  const { t } = useTranslation();
  const { orders } = props;
  const [orderBy, setOrderBy] = useState<TableKey>('id');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const navigate = useNavigate();
  const { retailerId } = useParams();

  const handleRequestSort = (_e, property: TableKey) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const createSortHandler = (property: TableKey) => (event) => {
    handleRequestSort(event, property);
  };

  const handleRowClick = (order: OOrder) => {
    navigate(`/documents/${retailerId}/orders/${order.id}`);
  };

  return (
    <div className="py-2-0">
      <StyledTable>
        {TABLE_HEAD(t).map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.alignRight ? 'right' : 'left'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              hideSortIcon
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              <span className="text-body1 font-rg opacity-80 capitalize">
                {headCell.label}
              </span>
              {orderBy === headCell.id ? (
                <Box sx={{ ...visuallyHidden }}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
        {orders.map((order) => (
          <TableRow
            hover
            key={order.id}
            tabIndex={-1}
            role="checkbox"
            className="relative"
          >
            <TableCell align="left">
              <span className="table-cell text-1-4">{order.id}</span>
            </TableCell>
            <TableCell align="left">
              <span
                className={clsx(
                  getStylesByColor(order.status),
                  'capitalize rounded-8-0 px-1-2 py-0-4 text-2-0 md:text-1-4 font-md',
                )}
              >
                {order.status.toLowerCase()}
              </span>
            </TableCell>
            <TableCell align="left">
              <span className="table-cell text-1-4">
                {fDateTime(order.confirmedAt)}
              </span>
            </TableCell>
            <TableCell align="left">
              <span className="table-cell text-1-4">
                {fToCurrency(order.totalNet || 0)}
              </span>
            </TableCell>
            <div
              className="absolute right-0 h-full flex items-center px-1-0 cursor-pointer opacity-70 hover:opacity-90"
              onClick={() => handleRowClick(order)}
            >
              <ReactSVG src={arrowTop} className="text-white rotate-90 " />
            </div>
          </TableRow>
        ))}
      </StyledTable>
      {orders.length === 0 && (
        <p className="text-body1 py-4-0 font-bd text-center">
          {t('order.order_not_found')}
        </p>
      )}
    </div>
  );
}
